import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** alertdialog for confirmations that interrupt; dialog otherwise. */
  role?: 'dialog' | 'alertdialog';
  children: React.ReactNode;
  /** Buttons, rendered right-aligned in the footer. */
  actions?: React.ReactNode;
  width?: number;
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Desktop modal: a centred card with a dimmed backdrop, Escape to close,
 * focus moved in on open and returned on close, Tab kept inside. Reserved
 * for moments that genuinely interrupt (delete, leave with unsaved changes);
 * everything else on desktop is a popover or inline.
 */
export function Dialog({ open, onClose, title, role = 'dialog', children, actions, width = 440 }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useRef(`dlg-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>('[data-autofocus]') ?? panel?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key === 'Tab' && panel) {
        const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (items.length === 0) return;
        const firstEl = items[0]; const lastEl = items[items.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
        else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      restoreRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/40 animate-fade-in" aria-hidden="true" />
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId.current}
        style={{ width, maxWidth: '100%' }}
        className="relative rounded-ios-card border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-ios-elevated animate-sheet-fade-in"
      >
        <div className="px-6 pt-5 pb-4">
          <h2 id={titleId.current} className="text-[17px] font-semibold text-[var(--text-primary)]">{title}</h2>
          <div className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">{children}</div>
        </div>
        {actions && (
          <div className="flex items-center justify-end gap-2 px-6 pb-5">{actions}</div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default Dialog;
